// module imports
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

// file imports
import axiosInstance from '@/lib/axios';
import { IEvent } from '@/types';

interface GetEventArgs {
  params?: Record<string, string | number>;
}

const addEvent = createAsyncThunk('adminEvent/addEvent', async (eventData: IEvent, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.post('/admin/events', eventData);
    return response.data;
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.error || 'Adding event failed');
  }
});

const getAllEvents = createAsyncThunk('adminEvent/getAllEvents', async ({ params = {} }: GetEventArgs, { rejectWithValue }) => {
  try {
    const query = new URLSearchParams(params as Record<string, string>).toString();
    const response = await axiosInstance.get(`/admin/events${query ? `?${query}` : ''}`);
    return response.data;
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.error || 'Retrieving events failed');
  }
});

const getEvent = createAsyncThunk('adminEvent/getEvent', async (slugOrId: string, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.get(`/admin/events/${slugOrId}`);
    return response.data;
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.error || 'Retrieving single event failed');
  }
});

const updateEvent = createAsyncThunk('adminEvent/updateEvent', async (params: { slugOrId: string; updatedData: any }, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.patch(`/admin/events/${params.slugOrId}`, params.updatedData);
    return response.data;
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.error || 'Updating event failed');
  }
});

const deleteEvent = createAsyncThunk('adminEvent/deleteEvent', async (slugOrId: string, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.delete(`/admin/events/${slugOrId}`);
    return response.data;
  } catch (err: any) {
    return rejectWithValue(err.response?.data?.error || 'Deleting event failed');
  }
});

const adminEventSlice = createSlice({
  name: 'adminEvent',
  initialState: {
    data: null as any,
    loading: false,
    error: null as string | null,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // addEvent
      .addCase(addEvent.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addEvent.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(addEvent.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // getAllEvents
      .addCase(getAllEvents.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllEvents.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(getAllEvents.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // getEvent
      .addCase(getEvent.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getEvent.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(getEvent.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // updateEvent
      .addCase(updateEvent.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateEvent.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(updateEvent.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // deleteEvent
      .addCase(deleteEvent.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteEvent.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(deleteEvent.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError } = adminEventSlice.actions;
export { addEvent, deleteEvent, getAllEvents, getEvent, updateEvent };
export default adminEventSlice.reducer;
